import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const INACTIVITY_WARNING_MS = 29 * 60 * 1000; // 29 minutes (warning before logout)
const INACTIVITY_LOGOUT_MS = 30 * 60 * 1000; // 30 minutes (auto-logout)
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';
const CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds (helps with throttled background tabs)

/**
 * Check if session has expired based on last activity timestamp
 * Called on page load to handle browser tab throttling
 */
export const checkSessionExpiredOnLoad = () => {
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  const token = sessionStorage.getItem('token');
  
  void 0 && console.log('[INACTIVITY] Check on load:', {
    hasLastActivity: !!lastActivity,
    hasToken: !!token,
    lastActivityValue: lastActivity,
    currentTime: Date.now()
  });
  
  // If there's no token, no need to check inactivity
  if (!token) {
    void 0 && console.log('[INACTIVITY] No token, skipping inactivity check');
    return false;
  }
  
  if (lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    void 0 && console.log('[INACTIVITY] Time since last activity:', Math.round(elapsed / 1000), 'seconds');
    
    if (elapsed > INACTIVITY_LOGOUT_MS) {
      // Session expired - clear token and last activity
      void 0 && console.log('[INACTIVITY] Session expired! Clearing token.');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem(LAST_ACTIVITY_KEY);
      return true; // Session was expired
    }
  }
  return false; // Session still valid
};

/**
 * Custom hook for managing inactivity timeout (HIPAA compliance)
 * 
 * Key improvements:
 * - Handles page visibility changes (tab switching)
 * - Periodic polling to compensate for browser timer throttling in background tabs
 * - Persists last activity timestamp to sessionStorage for cross-tab/refresh detection
 * 
 * @param {Function} logout - Logout function from auth context
 * @returns {Object} - State and handlers for inactivity management
 */
export const useInactivityTimeout = (logout) => {
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(60);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const isLoggedOutRef = useRef(false); // Prevent multiple logout calls

  // Update last activity timestamp in sessionStorage
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    void 0 && console.log('[INACTIVITY] Activity recorded at:', new Date(now).toLocaleTimeString());
  }, []);

  // Check elapsed time and trigger logout if needed
  const checkElapsedTime = useCallback(() => {
    if (isLoggedOutRef.current) return;
    
    const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    const token = sessionStorage.getItem('token');
    
    // No token = no session to timeout
    if (!token) {
      void 0 && console.log('[INACTIVITY] No token during check, skipping');
      return;
    }
    
    if (!lastActivity) {
      // No recorded activity but has token - record now
      updateLastActivity();
      return;
    }
    
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    const elapsedSeconds = Math.round(elapsed / 1000);
    const remainingSeconds = Math.round((INACTIVITY_LOGOUT_MS - elapsed) / 1000);
    
    void 0 && console.log('[INACTIVITY] Check: elapsed=' + elapsedSeconds + 's, remaining=' + remainingSeconds + 's');
    
    // Check if should log out
    if (elapsed >= INACTIVITY_LOGOUT_MS) {
      void 0 && console.log('[INACTIVITY] Session expired during periodic check');
      isLoggedOutRef.current = true;
      setShowInactivityWarning(false);
      toast.info('Session expired due to inactivity');
      logout();
      return;
    }
    
    // Check if should show warning (not already showing)
    if (elapsed >= INACTIVITY_WARNING_MS && !showInactivityWarning) {
      void 0 && console.log('[INACTIVITY] Showing warning');
      setShowInactivityWarning(true);
      setInactivityCountdown(Math.max(0, remainingSeconds));
    }
    
    // Update countdown if warning is showing
    if (showInactivityWarning) {
      setInactivityCountdown(Math.max(0, remainingSeconds));
    }
  }, [logout, showInactivityWarning, updateLastActivity]);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    // Prevent reset if already logged out
    if (isLoggedOutRef.current) return;
    
    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Update last activity timestamp for page refresh/visibility detection
    updateLastActivity();
    
    // Hide warning if shown
    setShowInactivityWarning(false);
    setInactivityCountdown(60);
    
    // Set warning timer (9 minutes)
    warningTimerRef.current = setTimeout(() => {
      if (isLoggedOutRef.current) return;
      setShowInactivityWarning(true);
      setInactivityCountdown(60);
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setInactivityCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_WARNING_MS);
    
    // Set logout timer (10 minutes)
    inactivityTimerRef.current = setTimeout(() => {
      if (isLoggedOutRef.current) return;
      // Auto logout
      isLoggedOutRef.current = true;
      setShowInactivityWarning(false);
      toast.info('Session expired due to inactivity');
      logout();
    }, INACTIVITY_LOGOUT_MS);
  }, [logout, updateLastActivity]);

  // Handle visibility change - critical for background tab detection
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      void 0 && console.log('[INACTIVITY] Tab became visible, checking elapsed time');
      checkElapsedTime();
    }
  }, [checkElapsedTime]);

  // Initialize timers on mount (separate effect to avoid lint warning)
  useEffect(() => {
    // Reset logged out flag and start initial timer on mount
    isLoggedOutRef.current = false;
    
    // Record initial activity
    const now = Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    
    // Set up initial timers
    const warningTimer = setTimeout(() => {
      if (isLoggedOutRef.current) return;
      setShowInactivityWarning(true);
      setInactivityCountdown(60);
      countdownIntervalRef.current = setInterval(() => {
        setInactivityCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_WARNING_MS);
    
    const logoutTimer = setTimeout(() => {
      if (isLoggedOutRef.current) return;
      isLoggedOutRef.current = true;
      setShowInactivityWarning(false);
      toast.info('Session expired due to inactivity');
      logout();
    }, INACTIVITY_LOGOUT_MS);
    
    // Store refs for cleanup
    warningTimerRef.current = warningTimer;
    inactivityTimerRef.current = logoutTimer;
    
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [logout]); // logout is stable from context

  // Set up activity listeners and periodic check
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      if (!showInactivityWarning && !isLoggedOutRef.current) {
        resetInactivityTimer();
      }
    };
    
    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Add visibility change listener for tab switching
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Set up periodic check to compensate for throttled timers in background tabs
    // Browsers throttle setTimeout in background tabs, so we need this fallback
    checkIntervalRef.current = setInterval(() => {
      checkElapsedTime();
    }, CHECK_INTERVAL_MS);
    
    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [resetInactivityTimer, showInactivityWarning, handleVisibilityChange, checkElapsedTime]);

  // Handle continue session
  const handleContinueSession = useCallback(() => {
    resetInactivityTimer();
    toast.success('Session continued');
  }, [resetInactivityTimer]);

  // Handle logout from warning
  const handleInactivityLogout = useCallback(() => {
    isLoggedOutRef.current = true;
    setShowInactivityWarning(false);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    sessionStorage.removeItem(LAST_ACTIVITY_KEY); // Clear activity timestamp on logout
    logout();
  }, [logout]);

  return {
    showInactivityWarning,
    inactivityCountdown,
    handleContinueSession,
    handleInactivityLogout,
    resetInactivityTimer
  };
};

export default useInactivityTimeout;
